/**
 * Dropped weapons — the gun a dead operator was holding falls to the ground.
 *
 * Small feature, disproportionate effect: a body with no rifle beside it reads as a
 * prop that was deleted, and a rifle lying where someone died is the clearest record
 * a shooter has of what just happened. It also tells you what killed you, which is
 * information the killfeed gives in words and the ground gives at a glance.
 *
 * ## Not rigid bodies
 *
 * These do not go through `PhysicsWorld`. A dropped weapon needs to fall, tumble,
 * land flat and then never move again — it is never pushed, never stood on, never
 * shot. Giving it a real collider would buy a resting contact solver we do not need
 * and a class of jitter we would then have to damp. Instead each one integrates its
 * own gravity, and the floor is found once with a single downward ray at the moment
 * it is dropped. Everything after that is a lerp onto a known resting height.
 *
 * The one thing that must be right is the resting height, because a rifle sunk into
 * the tarmac or hovering above it is worse than no rifle at all. So the probe starts
 * above the drop point rather than at it: a body that died on a crate is at crate
 * height, and a ray cast downward from its own feet would already be inside the
 * crate's top face and miss it.
 *
 * ## Budget
 *
 * `maxLive` oldest-out. Bodies persist for the whole match here, so without a cap a
 * long game accumulates rifles until the draw call count matters — and on the phone
 * tier, draw calls are the thing we just spent a release buying back.
 */

import * as THREE from 'three';

export const TUNING = {
  /** How many dropped weapons exist at once. Oldest is removed beyond this. */
  maxLive: 12,
  /** Seconds before one fades out. 0 keeps them for the whole match. */
  life: 45,
  fade: 1.2,

  gravity: -18,
  /** Initial toss, in the direction the victim was facing, plus a little up. */
  tossForward: 1.1,
  tossUp: 1.6,
  /** Radians/second of tumble while airborne. */
  spin: 7.0,
  /** Metres above the drop point the floor probe starts from. */
  probeUp: 1.4,
  probeDown: 6.0,
  /** How far the weapon rests above the surface it landed on. */
  restOffset: 0.045,
  /** Seconds to settle flat once it touches down. */
  settle: 0.22,
};

const _dir = new THREE.Vector3();
const _from = new THREE.Vector3();
const _down = new THREE.Vector3(0, -1, 0);

export class DroppedWeapons {
  constructor(ctx) {
    this.ctx = ctx;
    this.items = [];
    this.dropped = 0;

    this.group = new THREE.Group();
    this.group.name = 'dropped-weapons';
    ctx.scene.add(this.group);

    this._offs = [
      ctx.events.on('actor:death', (e) => this._onActorDeath(e)),
      // The player's own weapon. `game:state dead` is the signal the rest of the UI
      // already uses, so there is no new death event to keep in sync.
      ctx.events.on('game:state', (e) => {
        if (e?.state === 'dead') this._dropPlayerWeapon();
      }),
    ];

    ctx.engine.registerUpdate((dt) => this.update(dt), 'fx');
  }

  /* -------------------------------- drops --------------------------------- */

  _onActorDeath(e) {
    const actor = e?.actor;
    // The dev harnesses fire `actor:death` with a stub actor and no model; a missing
    // rifle is not an error, it just means there is nothing to drop.
    const rifle = actor?.model?.rifle;
    if (!rifle || !actor.position) return;

    const mesh = new THREE.Mesh(rifle.geometry, rifle.material);
    mesh.castShadow = true;
    // The enemy rifle hangs off a chest bone, so its world scale is whatever the
    // skinned hierarchy imposed. Copy it, or the weapon changes size as it falls.
    rifle.getWorldScale(mesh.scale);

    // Matches `Enemy.aimDir`, which is (-sin, 0, -cos) of yaw. Building it the
    // obvious way instead throws the rifle out of the back of the body.
    const yaw = actor.yaw ?? 0;
    _dir.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    this._spawn(mesh, actor.position, _dir, 1.05);
  }

  _dropPlayerWeapon() {
    const ws = this.ctx.weapons;
    const player = this.ctx.player;
    if (!ws || !player) return;

    const vm = ws.viewModel;
    const source = vm?.models?.get(ws.def?.model ?? ws.current?.id);
    const root = source?.root ?? source;
    if (!root?.clone) return;

    const mesh = root.clone(true);
    // View models are built for a camera 30 cm away and are drawn in their own scene
    // at their own scale. Dropped into the world they would be a monument, so they
    // are brought back to the world scale the enemy rifle already uses.
    mesh.scale.multiplyScalar(0.62);
    mesh.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = true;
        o.receiveShadow = false;
      }
    });

    const cam = this.ctx.camera;
    cam.getWorldDirection(_dir);
    _dir.y = 0;
    if (_dir.lengthSq() < 1e-6) _dir.set(0, 0, 1);
    _dir.normalize();
    this._spawn(mesh, player.position, _dir, 1.35);
  }

  /**
   * Put one weapon into the world.
   *
   * The floor probe runs here, once. Failing to find a floor is survivable — the
   * item simply rests at the height it was dropped from, which is wrong by at most
   * the height of whatever it died on and still better than deleting it.
   */
  _spawn(mesh, position, forward, dropHeight) {
    const physics = this.ctx.physics;
    let ground = position.y;
    if (physics?.raycast) {
      _from.set(position.x, position.y + TUNING.probeUp, position.z);
      const r = physics.raycast(_from, _down, TUNING.probeUp + TUNING.probeDown);
      if (r?.hit && r.point) ground = r.point.y;
    }

    mesh.position.set(position.x, position.y + dropHeight, position.z);
    mesh.rotation.set(0, Math.atan2(forward.x, forward.z), 0);
    this.group.add(mesh);

    const rng = this.ctx.rng ?? Math.random;
    this.items.push({
      mesh,
      vy: TUNING.tossUp * (0.7 + rng() * 0.5),
      vx: forward.x * TUNING.tossForward,
      vz: forward.z * TUNING.tossForward,
      spinX: (rng() * 2 - 1) * TUNING.spin,
      spinZ: (rng() * 2 - 1) * TUNING.spin * 0.5,
      restY: ground + TUNING.restOffset,
      landed: false,
      settleT: 0,
      age: 0,
      // The flat pose it eases into once it lands. Only yaw is kept — a rifle at
      // rest lies on its side, it does not stand on its magazine.
      restRotY: mesh.rotation.y + (rng() * 2 - 1) * 0.8,
      restRotZ: Math.PI * 0.5 * (rng() < 0.5 ? 1 : -1) * (0.75 + rng() * 0.35),
    });
    this.dropped++;

    while (this.items.length > TUNING.maxLive) this._remove(0);
  }

  _remove(i) {
    const it = this.items[i];
    if (!it) return;
    this.group.remove(it.mesh);
    // Geometry and materials are shared with the live weapon they were cloned from,
    // so disposing them here would blank the gun in someone's hands.
    this.items.splice(i, 1);
  }

  /* -------------------------------- frame --------------------------------- */

  update(dt) {
    if (!this.items.length) return;

    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      it.age += dt;

      if (!it.landed) {
        it.vy += TUNING.gravity * dt;
        const m = it.mesh;
        m.position.x += it.vx * dt;
        m.position.z += it.vz * dt;
        m.position.y += it.vy * dt;
        m.rotation.x += it.spinX * dt;
        m.rotation.z += it.spinZ * dt;

        if (m.position.y <= it.restY) {
          m.position.y = it.restY;
          it.landed = true;
          it.settleT = 0;
          this.ctx.events.emit('audio:play', {
            id: 'gear_drop',
            position: m.position.clone(),
            volume: 0.45,
          });
        }
      } else if (it.settleT < TUNING.settle) {
        // Ease from wherever the tumble left it into a plausible resting pose,
        // rather than snapping — a rifle that teleports flat on contact reads as a
        // glitch even though the end state is correct.
        it.settleT += dt;
        const k = Math.min(1, it.settleT / TUNING.settle);
        const e = k * k * (3 - 2 * k);
        const m = it.mesh;
        m.rotation.x += (0 - m.rotation.x) * e * 0.5;
        m.rotation.y += (it.restRotY - m.rotation.y) * e * 0.5;
        m.rotation.z += (it.restRotZ - m.rotation.z) * e * 0.5;
      }

      if (TUNING.life > 0) {
        const over = it.age - TUNING.life;
        if (over > TUNING.fade) {
          this._remove(i);
        } else if (over > 0) {
          const a = 1 - over / TUNING.fade;
          it.mesh.traverse((o) => {
            if (!o.isMesh) return;
            o.material.transparent = true;
            o.material.opacity = a;
          });
        }
      }
    }
  }

  report() {
    return {
      live: this.items.length,
      dropped: this.dropped,
      landed: this.items.filter((i) => i.landed).length,
    };
  }

  dispose() {
    for (const off of this._offs) off();
    this._offs.length = 0;
    while (this.items.length) this._remove(0);
    this.ctx.scene.remove(this.group);
  }
}
